import React from 'react';

import { useHistory } from 'react-router-dom';
import opcLogo from 'styles/assets/svg/opc-logo.svg';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import useUserHook from 'hooks/useUserHook';

import { useQueryCustomerGet } from 'api/queries/customerGet.query';
import { useQueryGetOpcProjectsList } from 'api/queries/getOpcProjectsList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';

import { Button } from 'components/DesignSystem/Button';
import { settingsConfig } from 'components/Settings/settingsConfig';
import { CustomizedTooltip } from 'components/common';

import classes from './OpcIntegrationCard.module.scss';

const DEFAULT_TOOLTIP_TEXT = 'Only users with the integrations admin role can manage this connection.';

export const OpcIntegrationCard = () => {
    const history = useHistory();
    const { isFetching, isSuccess } = useQueryGetOpcProjectsList();
    const { customerId } = useUserHook();
    const { data: customerData } = useQueryCustomerGet({ customerId });

    const onViewDetails = (): void => {
        history.push(
            AppRoutes.buildPath(
                AppRoutes.SETTINGS,
                settingsConfig.integrations.link,
                settingsConfig.opc.link,
                settingsConfig.viewOpc.link,
            ),
        );
    };

    const shouldShowConnectedState = !isFetching && isSuccess;
    const shouldShowConnectionIssueState = !isFetching && !isSuccess;

    return (
        <div className={classes.OpcIntegrationCard}>
            <div className={classes.header}>
                <div className={classes.content}>
                    <div className={classes.imageWrapper}>
                        <img className={classes.image} src={opcLogo} alt={'icon'} />
                    </div>
                    <div className={classes.text}>
                        <div className={classes.title}>Oracle Primavera Cloud</div>
                        <div className={classes.subtitle}>
                            Import and export schedules directly with Oracle Primavera Cloud.
                        </div>
                    </div>
                </div>

                <CustomizedTooltip
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={<div className={classes.tooltipContent}>{DEFAULT_TOOLTIP_TEXT}</div>}
                    triggerElement={
                        <div className={classes.infoIcon} aria-label={'integration-permissions-info'}>
                            <MuiIcon
                                icon={'info'}
                                fontSize={'2rem'}
                                color={'var(--fg-brand-primary)'}
                                type={'outlined'}
                            />
                        </div>
                    }
                />
            </div>

            <div className={classes.details}>
                <div className={classes.detailsColumn}>
                    <div className={classes.detailsItem}>
                        <div className={classes.detailsLabel}>Connected host:</div>
                        <div className={classes.detailsValue}>{customerData?.opcHost}</div>
                    </div>
                </div>

                <div className={classes.detailsColumn}>
                    <div className={classes.detailsItem}>
                        <div className={classes.detailsLabel}>Connected as:</div>
                        <div className={classes.detailsValue}>{customerData?.opcUsername}</div>
                    </div>
                </div>
            </div>

            <div className={classes.actions}>
                {isFetching ? (
                    <div className={classes.statusPill}>
                        <LoaderSpinner className={classes.statusSpinner} color={'var(--text-secondary)'} />
                        <span>Checking connection</span>
                    </div>
                ) : null}

                {shouldShowConnectedState ? (
                    <div className={`${classes.statusPill} ${classes.connectedPill}`}>
                        <MuiIcon
                            icon={'check_circle'}
                            fontSize={'1.8rem'}
                            color={'var(--text-success)'}
                            type={'outlined'}
                        />
                        <span>Connected</span>
                    </div>
                ) : null}

                {shouldShowConnectionIssueState ? (
                    <div className={`${classes.statusPill} ${classes.connectionIssuePill}`}>
                        <MuiIcon
                            icon={'error_outline'}
                            fontSize={'1.8rem'}
                            color={'var(--text-warning)'}
                            type={'outlined'}
                        />
                        <span>Connection Issue</span>
                    </div>
                ) : null}

                <Button variant={'primary'} onClick={onViewDetails}>
                    View Details
                </Button>
            </div>
        </div>
    );
};
